import { api } from "_shared/_stores/api";
import z from "zod";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import FeeScheduleTemplateSchema, { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import FeeScheduleTemplateCommand from "work/fee-schedule-templates/_api/commands/create-fee-schedule-template-api-request";
import UpdateFeeScheduleTemplateCommand from "work/fee-schedule-templates/_api/commands/update-fee-schedule-template-command";

export const feeScheduleTemplateApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getFeeScheduleTemplateById: build.query<FeeScheduleTemplateJSON, { feeScheduleTemplateId: string }>({
      query: ({ feeScheduleTemplateId }) => `work/fee-schedule-templates/${feeScheduleTemplateId}`,
      rawResponseSchema: FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, request) => [{ type: "FeeScheduleTemplate", id: request.feeScheduleTemplateId }],
    }),

    getFeeScheduleTemplatesByIds: build.query<FeeScheduleTemplateJSON[], { feeScheduleTemplateIds: string[] }>({
      query: ({ feeScheduleTemplateIds }) => ({
        url: "work/fee-schedule-templates/get-by-ids",
        params: feeScheduleTemplateIds,
      }),
      rawResponseSchema: z.array(FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
            // Tag each item
            ...(result?.map((item) => ({
              type: "FeeScheduleTemplate" as const,
              id: item.id ?? undefined,
            })) ?? []),
            // Tag the list itself
            { type: "FeeScheduleTemplate" as const, id: "LIST" },
          ]
          : [{ type: "FeeScheduleTemplate" as const, id: "LIST" }],
    }),

    getFeeScheduleTemplates: build.query<Paginated<FeeScheduleTemplateJSON>, { query: FeeScheduleTemplateQuery, interfaceName: string }>({
      query: ({ query }) => ({
        url: "work/fee-schedule-templates",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data: feeScheduleTemplatesResponse } = await queryFulfilled;
        for (const feeScheduleTemplateJSON of feeScheduleTemplatesResponse.data) {
          if (!feeScheduleTemplateJSON.id) return;
          dispatch(
            feeScheduleTemplateApi.util.upsertQueryData(
              "getFeeScheduleTemplateById",
              { feeScheduleTemplateId: feeScheduleTemplateJSON.id },
              feeScheduleTemplateJSON,
            ),
          );
        }
      },
      transformResponse: (response: FeeScheduleTemplateJSON[], meta): Paginated<FeeScheduleTemplateJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
            // Tag each item
            ...(result?.data?.map((item) => ({
              type: "FeeScheduleTemplate" as const,
              id: item.id ?? undefined,
            })) ?? []),
            // Tag the list itself
            { type: "FeeScheduleTemplate" as const, id: "LIST" },
          ]
          : [{ type: "FeeScheduleTemplate" as const, id: "LIST" }],
    }),
    // #endregion

    // #region Create
    createFeeScheduleTemplate: build.mutation<FeeScheduleTemplateJSON, { template: FeeScheduleTemplate }>({
      query: ({ template }) => ({
        url: "work/fee-schedule-templates",
        method: "POST",
        body: new FeeScheduleTemplateCommand(template).payload,
      }),
      rawResponseSchema: FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [
        { type: "FeeScheduleTemplate", id: "LIST" },
      ]
    }),
    // #endregion

    // #region Update
    updateFeeScheduleTemplate: build.mutation<
      FeeScheduleTemplateJSON,
      { templateId: string; originalTemplate: FeeScheduleTemplate; updatedTemplate: FeeScheduleTemplate }
    >({
      query: ({ templateId, originalTemplate, updatedTemplate }) => ({
        url: `work/fee-schedule-templates/${templateId}`,
        method: "PATCH",
        body: new UpdateFeeScheduleTemplateCommand(originalTemplate, updatedTemplate).payload,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async ({ templateId }, { dispatch, queryFulfilled }) => {
        const { data: feeScheduleTemplateJSON } = await queryFulfilled;
        dispatch(
          feeScheduleTemplateApi.util.upsertQueryData(
            "getFeeScheduleTemplateById",
            { feeScheduleTemplateId: templateId },
            feeScheduleTemplateJSON,
          ),
        );
      },
      invalidatesTags: () => [{ type: "FeeScheduleTemplate" as const, id: "LIST" }],
    }),
    // #endregion

    // #region Delete
    deleteFeeScheduleTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/fee-schedule-templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: "FeeScheduleTemplate" as const, id: templateId },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),
    // #endregion

    // #region Archive
    archiveFeeScheduleTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/fee-schedule-templates/${templateId}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: "FeeScheduleTemplate" as const, id: templateId },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),

    unarchiveFeeScheduleTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/fee-schedule-templates/${templateId}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: "FeeScheduleTemplate" as const, id: templateId },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetFeeScheduleTemplatesQuery,
  useGetFeeScheduleTemplateByIdQuery,
  useGetFeeScheduleTemplatesByIdsQuery,
  useCreateFeeScheduleTemplateMutation,
  useUpdateFeeScheduleTemplateMutation,
  useDeleteFeeScheduleTemplateMutation,
  useArchiveFeeScheduleTemplateMutation,
  useUnarchiveFeeScheduleTemplateMutation,
} = feeScheduleTemplateApi;
